<template>
  <div>
    <b-container
      class="load-simulation__download-template mb-3 no-internal-print"
      fluid
    >
      <b-row
        no-gutters
        align-h="end"
      >
        <b-col
          xl="6"
        >
          <b-row no-gutters>
            <b-col
              class="mr-1 mb-1"
              sm="7"
            >
              <s-loading-button
                block
                variant="outline-primary"
                @click="$event.wait(loadCurrentConditions())"
              >
                <label class="fas mr-2"/>
                {{ $t('engineering.load-simulation.load-current-conditions') }}
              </s-loading-button>
            </b-col>
            <b-col
              class="mr-1"
            >
              <b-button
                block
                variant="outline-green"
                v-b-tooltip="$t('messages.MI042.message')"
                @click="downloadTemplate"
              >
                <label class="fas fa-file-excel mr-2"/>
                {{ $t('actions.template-download') }}
              </b-button>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-container>

    <accordion-panel
      id="simulation-initial-conditions-accordion"
      class="simulation-initial-conditions__accordion mb-3"
      :collapsed.sync="collapsedInitialConditions"
    >
      <b-row class="pl-3" slot="title">
        <label class="mb-0">
          {{ $t('engineering.load-simulation.simulation-initial-conditions') }}
        </label>
      </b-row>
      <b-row
        class="mb-3"
      >
        <b-col
          md="8"
          sm="12"
        >
          <label>
            {{ `${$t('fields.engineering.load-simulation.oil-initial-temperature')}*` }}
          </label>
          <s-number-input
            class="load-simulation__input"
            v-model="$v.conditions.oilInitialTemperature.$model"
            :numeric-options="getNumericOptions('oilInitialTemperature')"
            :validation="$v.conditions.oilInitialTemperature"
            :invalid="$v.conditions.oilInitialTemperature.$error"
            :disabled="loading"
            @blur-native-proxy="$v.conditions.oilInitialTemperature.$touch()"
          />
          <b-form-invalid-feedback v-if="$v.conditions.oilInitialTemperature.$error">
            {{ getMA086(fields.oilInitialTemperature) }}
          </b-form-invalid-feedback>
        </b-col>
        <b-col
          md="4"
          sm="12"
        >
          <label>
            {{ `${$t('fields.engineering.load-simulation.winding-initial-temperature')}*` }}
          </label>
          <s-number-input
            class="load-simulation__input"
            v-model="$v.conditions.windingInitialTemperature.$model"
            :numeric-options="getNumericOptions('windingInitialTemperature')"
            :validation="$v.conditions.windingInitialTemperature"
            :invalid="$v.conditions.windingInitialTemperature.$error"
            :disabled="loading"
            @blur-native-proxy="$v.conditions.windingInitialTemperature.$touch()"
          />
          <b-form-invalid-feedback v-if="$v.conditions.windingInitialTemperature.$error">
            {{ getMA086(fields.windingInitialTemperature) }}
          </b-form-invalid-feedback>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          md="8"
          sm="12"
        >
          <label>
            {{ `${$t('fields.engineering.load-simulation.winding-time-constant')}*` }}
          </label>
          <s-number-input
            class="load-simulation__input"
            v-model="$v.conditions.windingTimeConstant.$model"
            :numeric-options="getNumericOptions('windingTimeConstant')"
            :validation="$v.conditions.windingTimeConstant"
            :invalid="$v.conditions.windingTimeConstant.$error"
            :disabled="loading"
            @blur-native-proxy="$v.conditions.windingTimeConstant.$touch()"
          />
          <b-form-invalid-feedback v-if="$v.conditions.windingTimeConstant.$error">
            {{ getMA086(fields.windingTimeConstant) }}
          </b-form-invalid-feedback>
        </b-col>
        <b-col
          md="4"
          sm="12"
        >
          <label>
            {{ `${$t('fields.engineering.load-simulation.winding-hotspot-factor')}*` }}
            <info-badge :translation-key="'messages.MI105.message'" />
          </label>
          <s-number-input
            class="load-simulation__input"
            v-model="$v.conditions.windingHotspotFactor.$model"
            :numeric-options="getNumericOptions('windingHotspotFactor')"
            :validation="$v.conditions.windingHotspotFactor"
            :invalid="$v.conditions.windingHotspotFactor.$error"
            :disabled="loading"
            @blur-native-proxy="$v.conditions.windingHotspotFactor.$touch()"
          />
          <b-form-invalid-feedback v-if="$v.conditions.windingHotspotFactor.$error">
            {{ getMA086(fields.windingHotspotFactor) }}
          </b-form-invalid-feedback>
        </b-col>
      </b-row>

      <s-fieldset
        class="mt-3"
        :legend="$t('engineering.load-simulation.momment-first-point-considered')"
      >
        <b-row>
          <b-col
            md="8"
            sm="12"
          >
            <label>
              {{ `${$t('fields.engineering.load-simulation.simulation-initial-date')}*` }}
            </label>
            <s-input-date-picker
              class="load-simulation__input"
              v-model="simulationInitialDateProxy"
              :state="$bsStateValidator($v.conditions.simulationInitialDate.$error)"
              :disabled="loading"
              @input="$v.conditions.simulationInitialDate.$touch()"
            />
            <b-form-invalid-feedback
              v-if="$v.conditions.simulationInitialDate.$error"
              class="d-block"
            >
              {{ getMA101(fields.simulationInitialDate) }}
            </b-form-invalid-feedback>
          </b-col>
          <b-col>
            <label>
              {{ `${$t('fields.engineering.load-simulation.simulation-initial-time')}*` }}
            </label>
            <s-input-time
              class="load-simulation__input"
              v-model="$v.conditions.simulationInitialTime.$model"
              :disabled="loading"
              v-bind:with-seconds="false"
              @update:initialTime="conditions.simulationInitialTime"
            />
          </b-col>
        </b-row>
      </s-fieldset>
    </accordion-panel>

    <accordion-panel
      id="simulation-humidity-parameters-conditions-accordion"
      class="simulation-humidity-parameters-conditions__accordion mb-3"
      :collapsed.sync="collapsedHumidityParameters"
    >
      <b-row class="pl-3" slot="title">
        <label class="mb-0">
          {{ $t('engineering.load-simulation.humidity-parameters') }}
        </label>
      </b-row>

      <b-row class="mb-3">
        <b-col>
          <b-checkbox
            v-model="$v.conditions.considerHumidity.$model"
            :disabled="loading"
            @change="onChangeConsiderHumidityHandler"
          >
            {{ getLabel('considerHumidity') }}
          </b-checkbox>
        </b-col>
      </b-row>

      <div
        class="px-4"
      >
        <b-row>
          <b-col
            sm="7"
            md="8"
          >
            <div class="form-group position-relative">
              <label>
                {{ getLabel('paperHumiditySource') }}
              </label>
              <s-select
                v-model="conditions.paperHumiditySource"
                :options="paperHumiditySourceOptions"
                :disabled="!conditions.enableHumidityParameters || loading"
                @change="onChangePaperHumidityHandler"
              />
            </div>
          </b-col>
          <b-col
            sm="5"
            md="4"
          >
            <div
              v-if="
                  conditions.enableHumidityParameters &&
                  conditions.enablePaperHumidity
                "
              class="load-simulation-parameters-general__input form-group"
            >
              <label>
                {{ getLabel('paperHumidity') }}
              </label>
              <s-number-input
                class="load-simulation__input text-right"
                v-model="$v.conditions.paperHumidity.$model"
                :numeric-options="getNumericOptions('paperHumidity')"
                :validation="$v.conditions.paperHumidity"
                :invalid="$v.conditions.paperHumidity.$error"
                :disabled="loading"
              />
              <b-form-invalid-feedback
                id="input-live-feedback-paper-humidity"
              >
                <label>{{ getMA086(fields.paperHumidity) }}</label>
              </b-form-invalid-feedback>
            </div>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            sm="7"
            md="8"
          >
            <div class="form-group position-relative">
              <label>
                {{ getLabel('referenceHumidityBase') }}
              </label>
              <s-enum-select
                v-model="conditions.referenceHumidityBase"
                no-select-option
                translation-path="enums.engineering.isolation-aging"
                :enum-definition="referenceHumidityBaseEnum"
                :disabled="!conditions.enableHumidityParameters || loading"
                @input="onChangeReferenceHumidityHandler"
              />
            </div>
          </b-col>
          <b-col
            sm="5"
            md="4"
          >
            <div
              v-if="
                  conditions.enableHumidityParameters
                    && conditions.enableReferenceHumidity
                  "
              class="load-simulation-parameters-general__input form-group"
            >
              <label>
                {{ getLabel('referenceHumidity') }}
              </label>
              <s-number-input
                class="load-simulation__input text-right"
                v-model="$v.conditions.referenceHumidity.$model"
                :numeric-options="getNumericOptions('referenceHumidity')"
                :validation="$v.conditions.referenceHumidity"
                :invalid="$v.conditions.referenceHumidity.$error"
                :disabled="loading"
              />
              <b-form-invalid-feedback
                id="input-live-feedback-reference-humidity-percentage"
              >
                <label>{{ getMA086(fields.referenceHumidity) }}</label>
              </b-form-invalid-feedback>
            </div>
          </b-col>
        </b-row>

        <b-row>
          <b-col
            sm="7"
            md="8"
          >
            <div class="form-group position-relative">
              <label>
                {{ getLabel('methodForHumidityCurve') }}
              </label>
              <s-enum-select
                v-model="conditions.methodForHumidityCurve"
                no-select-option
                translation-path="enums.engineering.isolation-aging"
                :enum-definition="methodForHumidityCurveEnum"
                :disabled="!conditions.enableHumidityParameters || loading"
                @input="onChangeHumidityCurveHandler"
              />
              <b-form-invalid-feedback
                id="input-live-feedback-humidity-curve-method"
              >
                <label>{{ getMA086(fields.methodForHumidityCurve) }}</label>
              </b-form-invalid-feedback>
            </div>
          </b-col>
          <b-col
            sm="5"
            md="4"
          >
            <div
              v-if="
                  conditions.enableHumidityParameters && conditions.enableOilOxygen
                "
              class="form-group position-relative">
              <label>
                {{ getLabel('oilOxygen') }}
              </label>
              <s-number-input
                class="load-simulation__input text-right"
                v-model="$v.conditions.oilOxygen.$model"
                :numeric-options="getNumericOptions('oilOxygen')"
                :validation="$v.conditions.oilOxygen"
                :invalid="$v.conditions.oilOxygen.$error"
                :disabled="loading"
              />
              <b-form-invalid-feedback
                id="input-live-feedback-oxygen-oil"
              >
                <label>{{ getMA086(fields.oilOxygen) }}</label>
              </b-form-invalid-feedback>
            </div>
          </b-col>
        </b-row>
      </div>
    </accordion-panel>

    <accordion-panel
      id="simulation-cooling-conditions-accordion"
      class="simulation-cooling-conditions__accordion mb-3"
      :collapsed.sync="collapsedCoolingConditions"
    >
      <b-row
        class="pl-3"
        slot="title">
        <label class="mb-0">
          {{ $t('engineering.load-simulation.cooling-conditions') }}
        </label>
      </b-row>

      <b-row>
        <b-col
          class="mb-3"
          sm="3"
          mb="3">
          <label>
            {{ `${$t('fields.engineering.load-simulation.cooling-command')}*` }}
          </label>
          <s-enum-select
            v-model="conditions.coolingCommand"
            no-select-option
            translation-path="enums.engineering.load-simulation.cooling-commands"
            :enum-definition="coolingCommandEnum"
            :disabled="loading"
            @input="onChangeCoolingCommandHandler"
          />
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col v-if="conditions.isCoolingCommandAuto">
          <label>{{
              $t('fields.engineering.load-simulation.stage-1')
            }}</label>
          <s-number-input
            class="load-simulation__input"
            v-model="$v.conditions.stage1.$model"
            :numeric-options="getNumericOptions('stage1')"
            :validation="$v.conditions.stage1"
            :invalid="$v.conditions.stage1.$error"
            :disabled="!conditions.enableStage1"
            @blur-native-proxy="$v.conditions.stage1.$touch()"
            @input="onUpdateHysteresis(false)"
          />
          <b-form-invalid-feedback v-if="$v.conditions.stage1.$error">
            {{ getMA086(fields.stage1) }}
          </b-form-invalid-feedback>
        </b-col>
        <b-col v-if="conditions.isCoolingCommandAuto">
          <label>{{
              $t('fields.engineering.load-simulation.stage-2')
            }}</label>
          <s-number-input
            class="load-simulation__input"
            v-model="$v.conditions.stage2.$model"
            :numeric-options="getNumericOptions('stage2')"
            :validation="$v.conditions.stage2"
            :invalid="$v.conditions.stage2.$error"
            :disabled="!conditions.enableStage2"
            @blur-native-proxy="$v.conditions.stage2.$touch()"
            @input="onUpdateHysteresis(false)"
          />
          <b-form-invalid-feedback v-if="$v.conditions.stage2.$error
          && validateStages('stage2')">
            {{ getMA086(fields.stage2) }}
          </b-form-invalid-feedback>
          <b-form-invalid-feedback v-if="$v.conditions.stage2.$error
          && !validateStages('stage2')">
            {{ getMA100('stage2') }}
          </b-form-invalid-feedback>
        </b-col>
        <b-col v-if="conditions.isCoolingCommandAuto">
          <label>{{
              $t('fields.engineering.load-simulation.stage-3')
            }}</label>
          <s-number-input
            class="load-simulation__input"
            v-model="$v.conditions.stage3.$model"
            :numeric-options="getNumericOptions('stage3')"
            :validation="$v.conditions.stage3"
            :invalid="$v.conditions.stage3.$error"
            :disabled="!conditions.enableStage3"
            @blur-native-proxy="$v.conditions.stage3.$touch()"
            @input="onUpdateHysteresis(false)"
          />
          <b-form-invalid-feedback v-if="$v.conditions.stage3.$error
          && validateStages('stage3')">
            {{ getMA086(fields.stage3) }}
          </b-form-invalid-feedback>
          <b-form-invalid-feedback v-if="$v.conditions.stage3.$error
          && !validateStages('stage3')">
            {{ getMA100('stage3') }}
          </b-form-invalid-feedback>
        </b-col>
        <b-col v-if="conditions.isCoolingCommandAuto">
          <label>{{
              $t('fields.engineering.load-simulation.stage-4')
            }}</label>
          <s-number-input
            class="load-simulation__input"
            v-model="$v.conditions.stage4.$model"
            :numeric-options="getNumericOptions('stage4')"
            :validation="$v.conditions.stage4"
            :invalid="$v.conditions.stage4.$error"
            :disabled="!conditions.enableStage4"
            @blur-native-proxy="$v.conditions.stage4.$touch()"
            @input="onUpdateHysteresis(false)"
          />
          <b-form-invalid-feedback v-if="$v.conditions.stage4.$error
          && validateStages('stage4')">
            {{ getMA086(fields.stage4) }}
          </b-form-invalid-feedback>
          <b-form-invalid-feedback v-if="$v.conditions.stage4.$error
          && !validateStages('stage4')">
            {{ getMA100('stage4') }}
          </b-form-invalid-feedback>
        </b-col>
      </b-row>

      <b-row
        v-if="conditions.isCoolingCommandAuto && conditions.enableHysteresis">
        <b-col
          sm="12"
        >
          <label>{{
              `${$t('fields.engineering.load-simulation.hysteresis-cooling-shutdown')}*`
            }}</label>
          <s-number-input
            class="load-simulation__input"
            v-model="$v.conditions.hysteresisCoolingShutdown.$model"
            :numeric-options="getNumericOptions('hysteresisCoolingShutdown')"
            :validation="$v.conditions.hysteresisCoolingShutdown"
            :invalid="$v.conditions.hysteresisCoolingShutdown.$error"
            :disabled="loading"
            @input="onUpdateHysteresis(false)"
          />
          <b-form-invalid-feedback v-if="$v.conditions.hysteresisCoolingShutdown.$error">
            {{ getMA086(fields.hysteresisCoolingShutdown) }}
          </b-form-invalid-feedback>
        </b-col>
      </b-row>
    </accordion-panel>

    <accordion-panel
      id="simulation-alarm-parameters-accordion"
      class="simulation-alarm-parameters__accordion mb-3 avoid-break"
      :collapsed.sync="collapsedAlarmParameters"
    >
      <b-row
        class="pl-3"
        slot="title">
        <label class="mb-0">
          {{ $t('engineering.load-simulation.alarm-parameters') }}
        </label>
      </b-row>
      <s-fieldset
        class="mt-3"
        :legend="$t('engineering.load-simulation.oil')">
        <b-row>
          <b-col
            md="8"
            sm="12"
          >
            <label>{{
                `${$t('fields.engineering.load-simulation.oil-alarm-temperature')}*`
              }}</label>
            <s-number-input
              class="load-simulation__input"
              v-model="$v.conditions.oilAlarmTemperature.$model"
              :numeric-options="getNumericOptions('oilAlarmTemperature')"
              :validation="$v.conditions.oilAlarmTemperature"
              :invalid="$v.conditions.oilAlarmTemperature.$error"
              :disabled="loading"
            />
            <b-form-invalid-feedback v-if="$v.conditions.oilAlarmTemperature.$error">
              {{ getMA086(fields.oilAlarmTemperature) }}
            </b-form-invalid-feedback>
          </b-col>
          <b-col
            md="4"
            sm="12"
          >
            <label>{{
                `${$t('fields.engineering.load-simulation.oil-shutdown-temperature')}*`
              }}</label>
            <s-number-input
              class="load-simulation__input"
              v-model="$v.conditions.oilShutdownTemperature.$model"
              :numeric-options="getNumericOptions('oilShutdownTemperature')"
              :validation="$v.conditions.oilShutdownTemperature"
              :invalid="$v.conditions.oilShutdownTemperature.$error"
              :disabled="loading"
            />
            <b-form-invalid-feedback v-if="$v.conditions.oilShutdownTemperature.$error">
              {{ getMA086(fields.oilShutdownTemperature) }}
            </b-form-invalid-feedback>
          </b-col>
        </b-row>
      </s-fieldset>
      <s-fieldset
        class="mt-3"
        :legend="$t('engineering.load-simulation.winding')">
        <b-row>
          <b-col
            md="8"
            sm="12"
          >
            <label>{{
                `${$t('fields.engineering.load-simulation.winding-alarm-temperature')}*`
              }}</label>
            <s-number-input
              class="load-simulation__input"
              v-model="$v.conditions.windingAlarmTemperature.$model"
              :numeric-options="getNumericOptions('windingAlarmTemperature')"
              :validation="$v.conditions.windingAlarmTemperature"
              :invalid="$v.conditions.windingAlarmTemperature.$error"
              :disabled="loading"
            />
            <b-form-invalid-feedback v-if="$v.conditions.windingAlarmTemperature.$error">
              {{ getMA086(fields.windingAlarmTemperature) }}
            </b-form-invalid-feedback>
          </b-col>
          <b-col
            md="4"
            sm="12"
          >
            <label>{{
                `${$t('fields.engineering.load-simulation.winding-shutdown-temperature')}*`
              }}</label>
            <s-number-input
              class="load-simulation__input"
              v-model="$v.conditions.windingShutdownTemperature.$model"
              :numeric-options="getNumericOptions('windingShutdownTemperature')"
              :validation="$v.conditions.windingShutdownTemperature"
              :invalid="$v.conditions.windingShutdownTemperature.$error"
              :disabled="loading"
            />
            <b-form-invalid-feedback v-if="$v.conditions.windingShutdownTemperature.$error">
              {{ getMA086(fields.windingShutdownTemperature) }}
            </b-form-invalid-feedback>
          </b-col>
        </b-row>
      </s-fieldset>
    </accordion-panel>

    <accordion-panel
      id="simulation-alarm-norms-accordion"
      class="simulation-alarm-norms__accordion"
      :collapsed.sync="collapsedNorms"
    >
      <b-row
        class="pl-3"
        slot="title">
        <label class="mb-0">
          {{ $t('engineering.load-simulation.using-norms') }}
        </label>
      </b-row>
      <div class="simulation-alarm-norms-dropdown mb-3">
        <label>
          {{ `${$t('fields.engineering.load-simulation.norm-end-life-criteria')}*` }}
        </label>
        <s-enum-select
          v-model="$v.conditions.normEndLifeCriteria.$model"
          no-sort
          no-select-option
          translation-path="enums.engineering.isolation-aging.criteria"
          :enum-definition="endLifeNormsEnum"
          :disabled="loading"
          @input="onChangeReferenceHumidityHandler"
        />
      </div>
      <div class="simulation-alarm-norms-dropdown">
        <label>
          {{ `${$t('fields.engineering.load-simulation.norm-temperature-calculation')}*` }}
        </label>
        <s-enum-select
          v-model="$v.conditions.normTemperatureCalculation.$model"
          no-select-option
          translation-path="enums.engineering.load-simulation.temperature-calculation"
          :enum-definition="temperatureNormsEnum"
          :disabled="loading"
          @input="onChangeReferenceHumidityHandler"
        />
      </div>
    </accordion-panel>
  </div>
</template>
<script>
import AccordionPanel from '@/components/AccordionPanel';
import InfoBadge from '@/components/icon/InfoBadge';
import conditionFields from '@/domain/data/installation/engineering/loadSimulation/conditionFields';
import validations from '@/domain/data/installation/engineering/loadSimulation/validations';
import Service
  from '@/domain/services/installation/engineering/loadSimulation/loadSimulationService';
import enumFilter from '@/filters/enumFilter';
import {
  HumiditySource,
  ReferenceHumidity,
  HumidityCurve,
  LoadSimulationCoolingCommand,
  EndLifeIsolationCriteria,
  LoadSimulationTemperatureCalculationNorm,
} from 'sigma-mappings';
import { grammaticalJoin } from '@/services/arrayUtils';
import eventBus from '@/services/globalEventBus';
import loadSimulationEvents from '@/domain/events/loadSimulation';
import {
  formatter as dateTimeFormatter,
} from '@/services/dateTime/datetimeService';
import { formats } from '@/constants/localizer';

export default {
  name: 'SimulationCondition',
  props: {
    paperHumiditySourceData: {
      type: Array,
    },
    conditions: {
      type: Object,
      required: true,
    },
    isInvalid: {
      type: Boolean,
      required: true,
    },
    isHysteresisInvalid: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    InfoBadge,
    AccordionPanel,
  },
  data: () => ({
    loading: false,
    collapsedInitialConditions: false,
    collapsedHumidityParameters: false,
    collapsedCoolingConditions: false,
    collapsedAlarmParameters: false,
    collapsedNorms: false,
    referenceHumidityBaseEnum: ReferenceHumidity,
    methodForHumidityCurveEnum: HumidityCurve,
    endLifeNormsEnum: EndLifeIsolationCriteria,
    temperatureNormsEnum: LoadSimulationTemperatureCalculationNorm,
    moneyInputKey: 0,
  }),
  created() {
    this.enableStages();
    this.init();
  },
  beforeDestroy() {
    this.deinit();
  },
  filters: {
    enumFilter,
  },
  validations() {
    const values = {
      forcedStageCoolingQuantity: this.conditions.forcedStageCoolingQuantity,
      stage1: this.conditions.stage1,
      stage2: this.conditions.stage2,
      stage3: this.conditions.stage3,
      stage4: this.conditions.stage4,
      coolingState: this.conditions.coolingCommand,
      paperHumiditySource: this.conditions.paperHumiditySource,
      referenceHumidityBase: this.conditions.referenceHumidityBase,
      methodForHumidityCurve: this.conditions.methodForHumidityCurve,
    };
    return validations(this.fields, values);
  },
  computed: {
    fields() {
      return conditionFields();
    },
    paperHumiditySourceOptions() {
      return (this.paperHumiditySourceData || []).map(item => ({
        text: this.$t(`enums.engineering.isolation-aging.${item.id}`),
        value: item.id,
      }));
    },
    coolingCommandEnum() {
      const options = {};
      // const stagedCoolingEdit = this.conditions.coolingCommand;

      Object.keys(LoadSimulationCoolingCommand).forEach((key, index) => {
        // if (stagedCoolingEdit !== '') {
        //   options[stagedCoolingEdit] = LoadSimulationCoolingCommand[stagedCoolingEdit];
        // }

        if (index <= this.conditions.forcedStageCoolingQuantity) {
          options[key] = LoadSimulationCoolingCommand[key];
        }
      });

      return options;
    },
    simulationInitialDateProxy: {
      get() {
        return this.conditions.simulationInitialDate;
      },
      set(value) {
        this.conditions.simulationInitialDate = value;
      },
    },
    formInvalid() {
      return this.$v.$invalid;
    },
  },
  methods: {
    init() {
      eventBus.on(
        loadSimulationEvents.refreshStageQuantity, this.onChangeCoolingCommandHandler, this,
      );
    },
    deinit() {
      eventBus.off(
        loadSimulationEvents.refreshStageQuantity, this.onChangeCoolingCommandHandler, this,
      );
    },
    assignData(data) {
      Object.keys(data).forEach((item) => {
        if (item === 'temperatureStages' && data[item]) {
          const { forcedStageCoolingQuantity } = this.conditions;
          const {
            stage1,
            stage2,
            stage3,
            stage4,
          } = data[item];
          this.conditions.stage1 = stage1 && forcedStageCoolingQuantity >= 1
            ? stage1
            : this.conditions.stage1;
          this.conditions.stage2 = stage2 && forcedStageCoolingQuantity >= 2
            ? stage2
            : this.conditions.stage2;
          this.conditions.stage3 = stage3 && forcedStageCoolingQuantity >= 3
            ? stage3
            : this.conditions.stage3;
          this.conditions.stage4 = stage4 && forcedStageCoolingQuantity >= 4
            ? stage4
            : this.conditions.stage4;
        }

        if (item !== 'temperatureStages') {
          this.conditions[item] = data[item] || this.conditions[item];

          if (item === 'coolingCommand') {
            this.onChangeCoolingCommandHandler(false);
          }
        }
      });

      this.conditions.enableHumidityParameters = this.conditions.considerHumidity;
      this.enableParameterFields();
      this.moneyInputKey += 1;
      this.callAlerts(data.fieldsNotLoaded);
    },
    callAlerts(fieldsNotLoaded) {
      if (fieldsNotLoaded.length === 0) {
        this.$pushAlert({ code: 'MS033' });
        return;
      }

      this.$pushAlert({
        code: 'MA094',
        messageParams: {
          0: grammaticalJoin(fieldsNotLoaded),
        },
      });
    },
    dateFormatter(dateTimeISO) {
      if (!dateTimeISO) return '';
      return dateTimeFormatter(dateTimeISO, formats.DATE_SHORT);
    },
    downloadTemplate() {
      Service.downloadTemplate();
    },
    enableParameterFields() {
      this.onChangePaperHumidityHandler();
      this.onChangeReferenceHumidityHandler();
      this.onChangeHumidityCurveHandler();
    },
    enableStages() {
      switch (this.conditions.forcedStageCoolingQuantity) {
        case 1:
          this.conditions.enableStage1 = true;
          this.conditions.enableHysteresis = true;
          break;
        case 2:
          this.conditions.enableStage1 = true;
          this.conditions.enableStage2 = true;
          this.conditions.enableHysteresis = true;
          break;
        case 3:
          this.conditions.enableStage1 = true;
          this.conditions.enableStage2 = true;
          this.conditions.enableStage3 = true;
          this.conditions.enableHysteresis = true;
          break;
        case 4:
          this.conditions.enableStage1 = true;
          this.conditions.enableStage2 = true;
          this.conditions.enableStage3 = true;
          this.conditions.enableStage4 = true;
          this.conditions.enableHysteresis = true;
          break;
        default:
          break;
      }
    },
    getEnumFirstValueByI18n(enumList) {
      const path = 'enums.engineering.isolation-aging';
      const i18nList = [];
      Object.values(enumList)
        .forEach(item => i18nList.push({
          i18n: this.$t(`${path}.${item}`),
          value: item,
        }));
      const [first] = i18nList.sort((a, b) => (a.i18n > b.i18n ? 1 : -1));
      return first.value;
    },
    getLabel(fieldName) {
      return this.$t(this.fields[fieldName].label);
    },
    getMA086(fieldName) {
      const options = {
        minimumFractionDigits: fieldName.precision,
        maximumFractionDigits: fieldName.precision || 0,
      };
      return this.$t('messages.MA086.message', {
        minValue: this.$n(fieldName.minValue, options),
        maxValue: this.$n(fieldName.maxValue, options),
      });
    },
    getMA100(fieldName) {
      let message;
      switch (fieldName) {
        case 'stage2':
          message = ['1'];
          break;
        case 'stage3':
          message = ['1', '2'];
          break;
        case 'stage4':
          message = ['1', '2', '3'];
          break;
        default:
          message = [];
      }

      return this.$t('messages.MA100.message', {
        0: grammaticalJoin(message),
      });
    },
    getMA101(field) {
      return this.$t('messages.MA101.message', {
        0: this.dateFormatter(field.minValue),
        1: this.dateFormatter(field.maxValue),
      });
    },
    getNumericOptions(fieldName) {
      return {
        decimalPlaces: this.fields[fieldName].precision || 0,
      };
    },
    loadCurrentConditions() {
      const { assetModuleId } = this.$route.params;
      this.loading = true;

      return Service.getCurrentConditions(assetModuleId).then((data) => {
        this.assignData(data);
        this.$v.$touch();
        this.$emit('loadCurrentConditions', true);
      }).finally(() => {
        this.loading = false;
      });
    },
    onChangeConsiderHumidityHandler(state) {
      this.conditions.enableHumidityParameters = state;

      if (state) {
        [this.conditions.paperHumiditySource] = Object
          .values((this.paperHumiditySourceOptions || [])
            .map(item => item.value));
        this.conditions.referenceHumidityBase = this.getEnumFirstValueByI18n(
          ReferenceHumidity,
        );
        this.conditions.methodForHumidityCurve = this.getEnumFirstValueByI18n(
          HumidityCurve,
        );

        this.enableParameterFields();
        return;
      }

      this.conditions.paperHumiditySource = null;
      this.conditions.referenceHumidityBase = null;
      this.conditions.methodForHumidityCurve = null;
      this.conditions.paperHumidity = null;
      this.conditions.referenceHumidity = null;
      this.conditions.oilOxygen = null;
    },
    onChangePaperHumidityHandler() {
      if (this.conditions.paperHumiditySource === HumiditySource.CUSTOM) {
        this.conditions.enablePaperHumidity = true;
        return;
      }

      this.conditions.enablePaperHumidity = false;
      this.conditions.paperHumidity = null;
      this.$v.conditions.paperHumidity.$reset();
    },
    onChangeReferenceHumidityHandler() {
      if (this.conditions.referenceHumidityBase === ReferenceHumidity.CUSTOM) {
        this.conditions.enableReferenceHumidity = true;
        return;
      }

      this.conditions.enableReferenceHumidity = false;
      this.conditions.referenceHumidity = null;
      this.$v.conditions.referenceHumidity.$reset();
    },
    onChangeHumidityCurveHandler() {
      if (this.conditions.methodForHumidityCurve === HumidityCurve.OXYGEN_DAN_MARTIN) {
        this.conditions.enableOilOxygen = true;
        return;
      }

      this.conditions.enableOilOxygen = false;
      this.conditions.oilOxygen = null;
      this.$v.conditions.oilOxygen.$reset();
    },
    onChangeCoolingCommandHandler(resetHysteresis = true) {
      this.conditions.isCoolingCommandAuto = false;
      this.conditions.enableStage1 = false;
      this.conditions.enableStage2 = false;
      this.conditions.enableStage3 = false;
      this.conditions.enableStage4 = false;
      this.conditions.enableHysteresis = false;

      if (this.conditions.coolingCommand === LoadSimulationCoolingCommand.AUTO) {
        this.conditions.isCoolingCommandAuto = true;
        this.enableStages();
      }

      if (resetHysteresis) {
        this.conditions.hysteresisCoolingShutdown = 0;
      }
    },
    onUpdateHysteresis(value) {
      if (value !== undefined) {
        this.$emit('update:is-hysteresis-invalid', value);
      }
    },
    validateStages(field) {
      return (this.$v.conditions[field].lessThanLast
        && this.conditions.coolingCommand === LoadSimulationCoolingCommand.AUTO);
    },
  },
  watch: {
    conditions(newValue) {
      this.$emit('update:conditions', newValue);
    },
    formInvalid(newValue) {
      this.$emit('update:is-invalid', newValue);
    },
  },
};
</script>
<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"

.load-simulation__input
  +media-min-md()
    max-width: 11em

</style>
